import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  KIND_LABELS,
  LAYOUT_LABELS,
  cardLabel,
  dimLabel,
  isBuiltinSpec,
  layoutOf,
  placementNotes,
  suggestTitle,
  type DimHeaders,
} from "@/features/obs/lib/chart-labels";
import type {
  ObsBuiltinCard,
  ObsCapabilities,
  ObsCardSpec,
  ObsChartKind,
  ObsChartLayout,
  ObsDim,
} from "@/types/obs";

/**
 * Choose which charts a report draws, and what each one is called.
 *
 * 🔑 THE LIST OF OFFERS COMES FROM THE SERVER. No compatibility rules live here:
 * whether a dataset can draw a treemap depends on the parsed figures, which only
 * the backend holds. Deciding it in the SPA would mean a second copy of the
 * matrix, drifting quietly until the picker offers a chart that publishes blank —
 * the failure the upload validator exists to prevent, reached from the other end.
 *
 * 🔑 «صف» OR «عمود» IS PER CHART, AND TWO IS THE MAXIMUM IN A ROW. That limit is
 * not enforced with an error, because it cannot be broken: the packer the public
 * screen runs pairs consecutive «صف» charts two at a time, and a third one simply
 * takes the full width. So the picker states what WILL happen under each chart
 * rather than refusing anything — see `placementNotes`.
 *
 * 🔴 BOTH TITLES ARE REQUIRED, and both arrive pre-filled. A heading with Arabic
 * and no English falls back to the Arabic on the English screens; that leak took
 * two sessions of screenshot review to find in August 2026, and nothing about the
 * page looks wrong when it happens. Pre-filling makes the requirement free —
 * accepting the suggestion is zero work — while still letting the operator write
 * their own wording, which is what was asked for.
 */
export function ObsChartPicker({
  options,
  capabilities,
  headers,
  charts,
  builtins = [],
  onChange,
}: {
  options: { kind: ObsChartKind; dim: ObsDim }[];
  capabilities: ObsCapabilities;
  headers: DimHeaders;
  charts: ObsCardSpec[];
  /** The cards this screen's own component draws. Empty for a created report. */
  builtins?: ObsBuiltinCard[];
  onChange: (next: ObsCardSpec[]) => void;
}) {
  /** Options not already used, so the same chart is not added twice. */
  const available = options.filter(
    (o) =>
      !charts.some(
        (c) => !isBuiltinSpec(c) && c.kind === o.kind && c.dim === o.dim,
      ),
  );

  const add = (value: string) => {
    const [kind, dim] = value.split("|") as [ObsChartKind, ObsDim];
    onChange([
      ...charts,
      {
        kind,
        dim,
        title: suggestTitle({ kind, dim }, capabilities, headers),
        // Full width by default: it is what every composed report has always
        // drawn, so adding a chart never re-flows the ones already placed.
        layout: "column",
      },
    ]);
  };

  /** A card the screen owns that the operator removed, so it can be put back. */
  const droppedBuiltins = builtins.filter(
    (b) => !charts.some((c) => isBuiltinSpec(c) && c.builtin === b.id),
  );

  const restore = (id: string) => {
    const card = builtins.find((b) => b.id === id);
    if (card) onChange([...charts, { builtin: card.id, layout: card.layout }]);
  };

  const patch = (i: number, next: Partial<ObsCardSpec>) =>
    onChange(charts.map((c, j) => (i === j ? { ...c, ...next } : c)));

  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= charts.length) return;
    const next = [...charts];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const incomplete = (c: ObsCardSpec) =>
    !isBuiltinSpec(c) && (!c.title?.ar?.trim() || !c.title?.en?.trim());

  /** Where each chart will actually land, in the chart list's own order. */
  const notes = placementNotes(charts);

  return (
    <div className="space-y-4">
      {charts.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          لم تُختر أي رسوم بعد. إن تركتها فارغة سيُختار تلقائيًا عرض مناسب
          للبيانات.
        </div>
      )}

      {charts.map((c, i) => (
        <div
          // Index keys: two charts can legitimately share a kind and a dimension
          // is the only other field, so there is no stable identity to key on —
          // and keying on a rendered value is what broke the language switch in
          // August 2026.
          key={i}
          className="space-y-3 rounded-lg border border-border p-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">
              {i + 1}. {cardLabel(c, builtins, headers).title}
              {isBuiltinSpec(c) && (
                <span className="ms-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                  بطاقة أصلية
                </span>
              )}
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="تحريك لأعلى"
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="تحريك لأسفل"
                disabled={i === charts.length - 1}
                onClick={() => move(i, 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="حذف الرسم"
                onClick={() => onChange(charts.filter((_, j) => j !== i))}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            {cardLabel(c, builtins, headers).note}
          </p>

          {/* The «صف / عمود» choice. Two buttons rather than a Select: there are
              exactly two options, both need their consequence spelled out, and a
              segmented control shows the current one without being opened. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">العرض:</span>
            <div
              role="group"
              aria-label={`عرض الرسم ${i + 1}`}
              className="flex overflow-hidden rounded-md border border-border"
            >
              {(["row", "column"] as ObsChartLayout[]).map((option) => (
                <Button
                  key={option}
                  type="button"
                  size="sm"
                  variant={layoutOf(c) === option ? "default" : "ghost"}
                  aria-pressed={layoutOf(c) === option}
                  className="rounded-none border-0 px-4"
                  onClick={() => patch(i, { layout: option })}
                >
                  {LAYOUT_LABELS[option].ar}
                </Button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">
              {LAYOUT_LABELS[layoutOf(c)].note}
            </span>
          </div>

          {/* 🔑 THE TRUTH, NOT THE REQUEST. A third «صف» chart in a run has no
              partner left, and this is where the operator finds that out — the
              two-per-row limit is a fact of the layout, not an error state. */}
          <p className="text-xs text-muted-foreground">↳ {notes[i]}</p>

          {/* 🔑 A BUILT-IN CARD HAS NO FIELDS. Its heading, its chart and its
              table are the screen's own code — the operator is choosing where it
              sits, not what it says. Showing empty title boxes here would invite
              an edit that cannot be honoured. */}
          {!isBuiltinSpec(c) && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`chart-${i}-ar`}>العنوان (عربي)</Label>
                <Input
                  id={`chart-${i}-ar`}
                  value={c.title?.ar ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      title: { ar: e.target.value, en: c.title?.en ?? "" },
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`chart-${i}-en`}>العنوان (إنجليزي)</Label>
                <Input
                  id={`chart-${i}-en`}
                  dir="ltr"
                  value={c.title?.en ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      title: { ar: c.title?.ar ?? "", en: e.target.value },
                    })
                  }
                />
              </div>
            </div>
          )}

          {incomplete(c) && (
            <p className="text-xs text-red-600">
              العنوان مطلوب بالعربية والإنجليزية معًا — عنوان بالعربية فقط سيظهر
              بالعربية في النسخة الإنجليزية من الموقع.
            </p>
          )}
        </div>
      ))}

      {droppedBuiltins.length > 0 && (
        <div className="space-y-1.5 rounded-lg border border-dashed border-border p-3">
          <Label>بطاقات أصلية أُخفيت</Label>
          <p className="text-xs text-muted-foreground">
            هذه بطاقات من تصميم الشاشة أزلتها من الترتيب. إعادتها تضعها في آخر
            الصفحة، ويمكنك تحريكها بعد ذلك.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {droppedBuiltins.map((b) => (
              <Button
                key={b.id}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => restore(b.id)}
              >
                <Plus className="size-3" /> {b.title.ar}
              </Button>
            ))}
          </div>
        </div>
      )}

      {available.length > 0 ? (
        <div className="space-y-1.5">
          <Label>إضافة رسم</Label>
          <Select value="" onValueChange={add}>
            <SelectTrigger>
              <SelectValue placeholder="اختر نوع الرسم والبُعد" />
            </SelectTrigger>
            <SelectContent>
              {available.map((o) => (
                <SelectItem
                  key={`${o.kind}|${o.dim}`}
                  value={`${o.kind}|${o.dim}`}
                >
                  {KIND_LABELS[o.kind].ar} — حسب {dimLabel(o.dim, headers)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            <Plus className="inline size-3" /> تُعرض فقط الرسوم التي تستطيع
            بيانات هذا التقرير رسمها — الباقي سيظهر فارغًا، فلا يُقترح.
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          أُضيفت كل الرسوم التي تدعمها بيانات هذا التقرير.
        </p>
      )}
    </div>
  );
}
